import type { RouteAttr, TripHistory } from "@/types/fleet";
import { summarizeRouteAttrs } from "@/lib/history/route-attr-columns";
import { canonicalizeDriverRefs, formatDriverRef, type DriverRef } from "@/lib/fleet/driver-ref";

export interface AggregatedTripReportRow {
  id: string;
  vehicleId: string;
  deviceId?: string;
  vehicleName: string;
  plateNumber?: string;
  /** Distinct drivers seen across every trip in the period, in first-seen order. */
  driverRefs: DriverRef[];
  distance: number;
  distanceTotal: number;
  duration: number;
  /** Exact summed seconds; `duration` stays rounded minutes for display. */
  durationSeconds: number;
  avgSpeed: number;
  maxSpeed: number;
  fuelTotal: number;
  /** Summed enriched inputs (`show_in_reports`) across routes. */
  attrs?: RouteAttr[];
}

/**
 * Repairs the driver references upstream truncated, before anything keys off them — `collectDriverRefs`
 * and `aggregateTripsByDriver` both bucket by `formatDriverRef`, so one driver left at several
 * truncation depths would otherwise split into that many rows, each holding a slice of the mileage.
 *
 * Trips whose refs did not change come back as the very same object, so the memoized History and
 * Reports tables see an unchanged array when the drivers list has nothing to recover.
 */
export function canonicalizeTripDriverRefs(
  trips: TripHistory[],
  canonicalIdByFragment: Map<string, string>,
): TripHistory[] {
  if (canonicalIdByFragment.size === 0) return trips;

  let changed = false;
  const out = trips.map((trip) => {
    if (!trip.driverRefs?.length) return trip;
    const driverRefs = canonicalizeDriverRefs(trip.driverRefs, canonicalIdByFragment);
    if (driverRefs === trip.driverRefs) return trip;
    changed = true;
    return { ...trip, driverRefs };
  });

  return changed ? out : trips;
}

/**
 * Unlike the identity fields, drivers cannot be taken from the first trip — a vehicle may have
 * been driven by different people across the period, so the whole bucket is unioned.
 */
export function collectDriverRefs(trips: TripHistory[]): DriverRef[] {
  const byKey = new Map<string, DriverRef>();
  for (const trip of trips) {
    for (const ref of trip.driverRefs ?? []) {
      const key = formatDriverRef(ref);
      if (!byKey.has(key)) byKey.set(key, ref);
    }
  }
  return [...byKey.values()];
}

function buildSummedReportAttrs(trips: TripHistory[]): RouteAttr[] {
  return summarizeRouteAttrs(trips).map((summary) => ({
    id: summary.id,
    value: summary.total,
    realValue: summary.total,
    displayValue: summary.displayTotal,
    displayTitle: summary.title,
    unit: summary.unit,
  }));
}

/** Summed/derived metrics for an arbitrary bucket of routes (vehicle, day, …). */
export function aggregateTripMetrics(trips: TripHistory[]): Pick<
  AggregatedTripReportRow,
  | "distance"
  | "distanceTotal"
  | "duration"
  | "durationSeconds"
  | "avgSpeed"
  | "maxSpeed"
  | "fuelTotal"
  | "attrs"
> {
  const distance = Math.round(trips.reduce((sum, trip) => sum + trip.distance, 0) * 100) / 100;
  const distanceTotal = Math.round(trips.reduce((sum, trip) => sum + trip.distanceTotal, 0) * 100) / 100;
  const duration = trips.reduce((sum, trip) => sum + trip.duration, 0);
  // Exact seconds where the route provided them; older/synthetic trips only carry rounded minutes.
  const durationSeconds = trips.reduce(
    (sum, trip) => sum + (trip.durationSeconds ?? trip.duration * 60),
    0,
  );
  const distSum = trips.reduce((sum, trip) => sum + trip.distanceTotal, 0);
  const avgSpeed =
    distSum > 0
      ? Math.round(
          (trips.reduce((sum, trip) => sum + trip.distanceTotal * trip.avgSpeed, 0) / distSum) * 10,
        ) / 10
      : trips.length > 0
        ? Math.round((trips.reduce((sum, trip) => sum + trip.avgSpeed, 0) / trips.length) * 10) / 10
        : 0;
  const maxSpeed =
    trips.length === 0
      ? 0
      : Math.round(Math.max(...trips.map((trip) => trip.maxSpeed)) * 10) / 10;
  const fuelTotal = Math.round(trips.reduce((sum, trip) => sum + trip.fuelTotal, 0) * 100) / 100;

  return {
    distance,
    distanceTotal,
    duration,
    durationSeconds,
    avgSpeed,
    maxSpeed,
    fuelTotal,
    attrs: buildSummedReportAttrs(trips),
  };
}

/**
 * Each trip must already carry enriched per-route `attrs` from `/api/routes`
 * (`show_in_reports` inputs calculated per route). This sums those values per vehicle.
 */
export function aggregateTripsByVehicle(trips: TripHistory[]): AggregatedTripReportRow[] {
  const groups = new Map<string, TripHistory[]>();

  for (const trip of trips) {
    const list = groups.get(trip.vehicleId) ?? [];
    list.push(trip);
    groups.set(trip.vehicleId, list);
  }

  const rows: AggregatedTripReportRow[] = [];

  for (const [vehicleId, vehicleTrips] of groups) {
    if (vehicleTrips.length === 0) continue;

    rows.push({
      id: `${vehicleId}-report`,
      vehicleId,
      deviceId: vehicleTrips[0].deviceId,
      vehicleName: vehicleTrips[0].vehicleName,
      plateNumber: vehicleTrips[0].plateNumber,
      driverRefs: collectDriverRefs(vehicleTrips),
      ...aggregateTripMetrics(vehicleTrips),
    });
  }

  return rows.sort((a, b) => a.vehicleName.localeCompare(b.vehicleName));
}

/** One driver's routes, summed. Vehicle identity is a list here: a driver may cover several. */
export interface AggregatedDriverReportRow
  extends Pick<
    AggregatedTripReportRow,
    | "distance"
    | "distanceTotal"
    | "duration"
    | "durationSeconds"
    | "avgSpeed"
    | "maxSpeed"
    | "fuelTotal"
    | "attrs"
  > {
  /** `formatDriverRef` of {@link driverRef} — stable, and distinct between an id and a code. */
  id: string;
  driverRef: DriverRef;
  /** Routes attributed to this driver. */
  tripCount: number;
  /** Vehicles the driver was reported on, in first-seen order. */
  vehicles: { vehicleId: string; vehicleName: string }[];
}

/**
 * Routes bucketed by the driver(s) upstream reported on them, then summed with the same
 * {@link aggregateTripMetrics} the vehicle report uses.
 *
 * A route naming two drivers counts in full for **both** — upstream gives no split, and halving a
 * distance nobody measured would be a worse guess than double-counting a shared shift. The
 * consequence is that summing a driver column does not have to equal the fleet total.
 *
 * Routes with no driver reference produce no row: this aggregates *by driver*, and a route nobody
 * was tagged on has none to attribute. Such routes still appear in the trip and vehicle reports.
 */
export function aggregateTripsByDriver(trips: TripHistory[]): AggregatedDriverReportRow[] {
  const groups = new Map<string, { ref: DriverRef; trips: TripHistory[] }>();

  for (const trip of trips) {
    for (const ref of trip.driverRefs ?? []) {
      const key = formatDriverRef(ref);
      const bucket = groups.get(key);
      if (bucket) bucket.trips.push(trip);
      else groups.set(key, { ref, trips: [trip] });
    }
  }

  const rows: AggregatedDriverReportRow[] = [];

  for (const [key, { ref, trips: driverTrips }] of groups) {
    const vehicles = new Map<string, string>();
    for (const trip of driverTrips) {
      if (!vehicles.has(trip.vehicleId)) vehicles.set(trip.vehicleId, trip.vehicleName);
    }

    rows.push({
      id: key,
      driverRef: ref,
      tripCount: driverTrips.length,
      vehicles: [...vehicles].map(([vehicleId, vehicleName]) => ({ vehicleId, vehicleName })),
      ...aggregateTripMetrics(driverTrips),
    });
  }

  return rows;
}

/** Sum all enriched routes into one totals row (optional grand total). */
export function aggregateAllTrips(trips: TripHistory[]): AggregatedTripReportRow | null {
  if (trips.length === 0) return null;

  return {
    id: "all-report",
    vehicleId: "all",
    vehicleName: trips.length === 1 ? trips[0].vehicleName : `${trips.length} routes`,
    driverRefs: collectDriverRefs(trips),
    ...aggregateTripMetrics(trips),
  };
}
